import React, { FC, ReactNode, useMemo } from "react";
import { AttributeSelectorType, AttributeType, GeometryType } from "@evergis/api";
import { Dropdown, NumberInput, IOption } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import { pointOptions, defaultValue } from "./FormatSettingsOptions";
import { PointFormState } from "../hooks/useAttributeFormat";
import { updatePointFormat } from "../hooks/useUpdateAttributeFormat";
import { formatAttributeValue } from "../../../../../utils/formatAttributeValue";
import { FormSettingsContainer, Hint, SmallHintText } from "./styled";
import { FeaturedDcExtendedGeometry } from "../../../../../ducks/selectFeatures/types";

type PointFormatSettingsProps = {
  type: AttributeType;
  formState: PointFormState;
  attributeSelectorType?: AttributeSelectorType;
  childrenBefore?: ReactNode;
  onChangeFormatValue: (option: IOption[]) => void;
  onChangeNumberInput: (value: number) => void;
};

export const example: FeaturedDcExtendedGeometry = {
  type: GeometryType.Point,
  coordinates: [51.533562, 46.034266],
};

export const PointFormatSettings: FC<PointFormatSettingsProps> = ({
  type,
  formState,
  onChangeFormatValue,
  onChangeNumberInput,
  children,
}) => {
  const { t } = useTranslation("dialogs");

  const formattedExample = useMemo(
    () => formatAttributeValue(type, example, updatePointFormat(formState)),
    [type, example, formState],
  );

  const disabledRound = useMemo(() => formState.format !== pointOptions[2].value, [formState.format]);

  return (
    <FormSettingsContainer height={"19.125rem"}>
      <Dropdown
        label={`${t("dialogComponents.display")}:`}
        options={pointOptions.map(item => ({
          ...item,
          text: t(item.localizationKey, { ns: "common" }),
        }))}
        value={formState.format || defaultValue}
        width={"18rem"}
        zIndex={10000}
        onChange={onChangeFormatValue}
      />
      <NumberInput
        label={`${t("dialogComponents.fixedNumberTo")}:`}
        disabled={disabledRound}
        width={"9.1875rem"}
        value={formState.rounding}
        min={0}
        max={10}
        counter
        onChange={onChangeNumberInput}
      />
      <Hint>
        <SmallHintText>{t("dialogComponents.example")}:</SmallHintText>
        {formattedExample || example.coordinates}
      </Hint>
      {children}
    </FormSettingsContainer>
  );
};
